import React, { createContext, ReactNode, useMemo, useState } from 'react';
import {DraggableProvidedDragHandleProps} from '@hello-pangea/dnd';
import {DragIndicatorVerticalIcon, iconMap} from './Icons';
import {useAuth} from '../security/AuthProvider';

// ── Widget actions context ─────────────────────────────────────────────────────
//
// Widget components call useWidgetActions() to fill this slot from inside.
// WidgetCard is the provider; the widget body is the consumer.
// The context value is memoized so it never changes reference — this ensures
// that consuming the context does not cause the widget body to re-render when
// the header actions state changes.

interface WidgetActionsContextValue {
    setActions: (actions: ReactNode) => void;
}

export const WidgetActionsContext = createContext<WidgetActionsContextValue>({
    setActions: () => {},
});

// ── Pin icon ──────────────────────────────────────────────────
const PinIcon: React.FC = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="17" x2="12" y2="22"/>
        <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1
                 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2
                 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
    </svg>
);

// ── Props ─────────────────────────────────────────────────────
type WidgetCardProps = {
    title: string;
    icon?: string;
    isCollapsed?: boolean;
    children?: React.ReactNode;
    requiredPermissions?: string[];
    /** Spread onto the drag-handle element; null when isDragDisabled */
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    /** When true the widget cannot be moved or removed */
    isPinned?: boolean;
    /** Called when the user clicks the remove (×) button */
    onRemove?: () => void;
};

const WidgetCard: React.FC<WidgetCardProps> = ({
                                                   title,
                                                   icon,
                                                   isCollapsed = false,
                                                   children,
                                                   requiredPermissions,
                                                   dragHandleProps,
                                                   isPinned = false,
                                                   onRemove,
                                               }) => {

    const {hasAnyAuthority} = useAuth();

    // Actions are populated by child widget components via useWidgetActions().
    // The setter is stable (from useState), so the memoized context value never
    // changes reference — consuming the context won't re-render the widget body.
    const [actions, setActions] = useState<ReactNode>(null);
    const contextValue = useMemo(() => ({ setActions }), []);

    if (requiredPermissions && requiredPermissions.length > 0 &&
        !hasAnyAuthority(requiredPermissions)) {
        return null;
    }

    return (
        <WidgetActionsContext.Provider value={contextValue}>
            <div className="widget-card">
                {/* ── Header ── */}
                <div className="widget-card__header">

                    {/* Drag handle — spread dragHandleProps here, not on the whole card */}
                    {!isPinned && (
                        <span
                            className="widget-card__drag-handle"
                            title={'Widget verschieben'}
                            {...(dragHandleProps ?? {})}
                        >
                            <DragIndicatorVerticalIcon/>
                        </span>
                    )}

                    {/* Title + icon */}
                    <div className="widget-card__title-area">
                        {icon && iconMap[icon]}
                        <span className="widget-card__title">{title}</span>
                    </div>

                    {/* Actions registered by the widget component via useWidgetActions() */}
                    <div className="widget-card__actions">
                        {actions}
                    </div>
                </div>

                {/* ── Body ── */}
                {!isCollapsed && (
                    <div className="widget-card__body">
                        {children}
                    </div>
                )}
            </div>
        </WidgetActionsContext.Provider>
    );
};

export default WidgetCard;